/**
 * Presentational leaves shared by the two order surfaces.
 *
 * The Orders list panel and views/Order.jsx stay separate containers on
 * purpose — the panel answers "is this the order I meant" at 460px, the page is
 * the record of truth — but the leaves they both draw (money, dates, the status
 * and channel badges) had drifted into two copies. They live here now.
 *
 * Colours come from the shell's CSS variables where the element is inside the
 * page (var(--muted) and friends); anything that can portal to <body> takes a
 * literal hex, because a page-scoped custom property cannot reach it.
 */

import React from 'react';
import dayjs from 'dayjs';
import { OrderStatusEnum, OPEN_STATUSES } from '../../utils/enums/OrderStatusEnum';
import { useStateContext } from '../../contexts/ContextProvider';
import { OrderTypeEnum } from '../../utils/enums/OrderTypeEnum';

export const fmtBDT = (n) => `৳${(Math.round(Number(n) || 0)).toLocaleString('en-US')}`;

export const fmtDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '—');

export const fmtDateTime = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—');

/**
 * Compact form for table cells, where two date columns compete for width. The
 * year is dropped for the current year — nearly every row — and shown only when
 * it would otherwise be ambiguous. The full date goes in a title tooltip.
 */
export const fmtDateCell = (d) => {
  if (!d) return '—';
  const x = dayjs(d);
  return x.year() === dayjs().year() ? x.format('D MMM') : x.format('D MMM YY');
};

export const hexToRgba = (hex, a) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return `rgba(148,163,184,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
};

/**
 * A status's label and the colours for the current theme.
 *
 * `color` is the badge/text/dot value and flips with the theme; `solid` is the
 * fill for anything that paints the hue behind white text — the stepper circle
 * and the timeline tag — and is the light value in both themes, because that is
 * the one dark enough to carry white. Callers that only want the label can omit
 * `darkMode` and get the light value, which is the safe default on an unknown
 * ground.
 */
export const statusMeta = (val, darkMode = false) => {
  const s = OrderStatusEnum.find((x) => x.value === val)
    || { label: 'Unknown', value: val, light: '#57534e', dark: '#a8a29e' };

  return { ...s, color: darkMode ? s.dark : s.light, solid: s.light };
};

// The production span a stepper walks, in flow order (ADR 0006). HOLD and the
// exits are deliberately off it — they are departures from the line, not steps
// along it.
export const MAIN_FLOW = Object.freeze([1, 2, 12, 3, 13, 5, 9, 10, 6]);

/**
 * The hold categories, in the order the status modal offers them. Shared so the
 * modal that collects one and the surfaces that read one back — the hold banner
 * and the timeline — cannot disagree about the wording.
 */
export const HOLD_REASONS = Object.freeze([
  { value: 'REPAIR', label: 'Repair' },
  { value: 'RESCHEDULE', label: 'Reschedule' },
  { value: 'FABRIC_OUT_OF_STOCK', label: 'Fabric out of stock' },
  { value: 'OTHER', label: 'Other' },
]);

/** Label for a stored hold category; null for holds that predate the column. */
export const holdReasonLabel = (value) =>
  HOLD_REASONS.find((r) => r.value === value)?.label ?? null;

/**
 * The status stepper's model.
 *
 * Progress is a *position* on the line, not a set of ids that appear in the
 * history. The history is not a complete record of the path an order took:
 * DRAFT is written by no one, StockService auto-advances a fully-stocked order
 * straight to READY (ADR 0003), and a CHANGE_STATUS holder can jump the chain —
 * so treating "has a history row" as "reached" punched holes in the middle of
 * the line for exactly the orders that moved fastest.
 *
 * The furthest position any status the order is known to have held maps to is
 * the honest answer, and every step behind it is done. Off-flow statuses (HOLD
 * and the exits) contribute nothing themselves, which is why a held order is
 * placed by `heldFromStatus` — the stage it will be released back to.
 */
export const buildStatusSteps = ({ status, heldFromStatus = null, history = [] }) => {
  const known = [...(history || []).map((t) => t.status), status, heldFromStatus];

  // Index 0 (DRAFT) is the floor: every order has been a draft, whether or not
  // anything recorded it.
  const reached = known
    .map((val) => MAIN_FLOW.indexOf(val))
    .reduce((furthest, i) => (i > furthest ? i : furthest), 0);

  // An order that has left the line — held, returned, cancelled — gets a node of
  // its own at the end. Without one, a returned order rendered as a complete
  // delivery with every step ticked and nothing to say it came back: the exit
  // existed only in the badge above the stepper.
  const exitStatus = MAIN_FLOW.indexOf(status) === -1 ? status : null;
  const line = exitStatus === null ? [...MAIN_FLOW] : [...MAIN_FLOW, exitStatus];
  const lastFlow = MAIN_FLOW.length - 1;

  return line.map((val, i) => {
    const isExit = exitStatus !== null && i === line.length - 1;
    const current = isExit || val === status;
    const info = statusMeta(val);   // label only; the caller picks the colour
    return {
      value: val,
      label: info.label,
      exit: isExit,
      // A step the order has moved past. An order sent backwards keeps the
      // steps it did reach filled — the line records how far it got, not only
      // where it sits now.
      done: !isExit && i <= reached && !current,
      current,
      hasLine: i < line.length - 1,
      // The segment into the exit node is drawn only when the order actually
      // left from the last step on the line; a return from READY must not draw
      // a path through BOOKING and OUT FOR DELIVERY it never took.
      lineDone: exitStatus !== null && i === lastFlow ? i <= reached : i < reached,
    };
  });
};

/** Delivery dates before today on an order that is still open read as late. */
export const isOverdue = (deliveryDate, status) =>
  !!deliveryDate && OPEN_STATUSES.includes(status) && dayjs(deliveryDate).isBefore(dayjs(), 'day');

/**
 * The status badge. `compact` drops the leading dot so a 38px row keeps its
 * line; `onClick` turns it into the trigger for the status menu.
 */
export function StatusBadge({ status, compact = false, interactive = false, onClick, title }) {
  // Read the theme here rather than threading it through five call sites; the
  // badge is always rendered inside the shell, which provides it.
  const { darkMode } = useStateContext();
  const st = statusMeta(status, darkMode);
  return (
    <span
      onClick={onClick}
      title={title || st.label}
      style={{
        font: `700 ${compact ? 11 : 11.5}px Outfit, sans-serif`,
        color: st.color,
        background: hexToRgba(st.color, 0.16),
        padding: compact ? '3px 8px' : '4px 8px',
        borderRadius: compact ? 10 : 11,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        maxWidth: '100%',
        cursor: interactive ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {!compact && <span style={{ width: 6, height: 6, borderRadius: 3, background: st.color, flexShrink: 0 }} />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.label}</span>
      {interactive && <span style={{ fontSize: 8, opacity: 0.7, flexShrink: 0 }}>▾</span>}
    </span>
  );
}

/**
 * Channel cell. Online orders show the marketplace they came through (the page
 * name is the useful detail); offline ones carry the label alone. `compact`
 * suppresses the marketplace line so the row stays single-height.
 */
/**
 * How long a held order has been held, for a list row. Reads like the overdue
 * delivery marker — quiet until it crosses the threshold, then red. `tooLong`
 * comes from the server so the row and the "N over 7 days" count can't disagree
 * about which orders are stuck.
 */
export function HoldAge({ hold }) {
  if (!hold) return null;
  const reason = holdReasonLabel(hold.reason);
  return (
    <span
      title={reason ? `On hold for ${reason.toLowerCase()}` : 'On hold'}
      style={{
        font: `${hold.tooLong ? 700 : 500} 11px Outfit, sans-serif`,
        color: hold.tooLong ? '#ef4444' : 'var(--dim)',
        whiteSpace: 'nowrap',
      }}
    >
      {hold.days === 0 ? 'Held today' : `Held ${hold.days}d`}
    </span>
  );
}

/**
 * How long an order has been on the production floor (PRINT / PRODUCTION).
 * Reads like HoldAge: quiet until it hits 48 hours, then red error text.
 */
export function ProductionAge({ delay }) {
  if (!delay) return null;
  const hours = delay.hours ?? 0;
  const label = hours < 24 ? `${hours}h in prod` : `${Math.floor(hours / 24)}d in prod`;

  return (
    <span
      title={`In production for ${hours} hours`}
      style={{
        font: `${delay.delayed ? 700 : 500} 11px Outfit, sans-serif`,
        color: delay.delayed ? '#ef4444' : 'var(--dim)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export function ChannelBadge({ orderType, marketplace, compact = false }) {
  const isOffline = orderType === OrderTypeEnum.MERCHANT;
  const tint = isOffline ? '#f59e0b' : '#0ea5e9';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}>
      <span
        style={{
          alignSelf: 'flex-start',
          font: '700 11px Outfit, sans-serif',
          color: tint,
          background: hexToRgba(tint, 0.14),
          padding: '1px 6px',
          borderRadius: 8,
          whiteSpace: 'nowrap',
        }}
      >
        {isOffline ? 'Offline' : 'Online'}
      </span>
      {!compact && !isOffline && marketplace && (
        <span
          title={marketplace}
          style={{
            font: '400 11px Outfit, sans-serif',
            color: 'var(--dim)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {marketplace}
        </span>
      )}
    </span>
  );
}
